var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useState } from "react";
import { CheckmarkIcon, FilesIcon } from "@navikt/aksel-icons";
import { Button } from "../button/index.js";
import { useRenameCSS, useThemeInternal } from "../theme/Theme.js";
import { Label } from "../typography/index.js";
import { composeEventHandlers } from "../util/composeEventHandlers.js";
import copy from "../util/copy.js";
import { useTimeout } from "../util/hooks/useTimeout.js";
import { useI18n } from "../util/i18n/i18n.hooks.js";
/**
 * A button component that copies text to the clipboard when clicked.
 *
 * @see [📝 Documentation](https://aksel.nav.no/komponenter/core/copybutton)
 * @see 🏷️ {@link CopyButtonProps}
 *
 * @example
 * ```jsx
      <CopyButton copyText="3.14" />
 * ```
 */
export const CopyButton = forwardRef((_a, ref) => {
    var { className, copyText, text, activeText, variant = "neutral", onActiveChange, icon, activeIcon, activeDuration = 2000, title, iconPosition = "left", onClick, size = "medium" } = _a, rest = __rest(_a, ["className", "copyText", "text", "activeText", "variant", "onActiveChange", "icon", "activeIcon", "activeDuration", "title", "iconPosition", "onClick", "size"]);
    const [active, setActive] = useState(false);
    const translate = useI18n("CopyButton");
    const timeout = useTimeout();
    const { cn } = useRenameCSS();
    const themeContext = useThemeInternal(false);
    const handleClick = () => {
        copy(copyText);
        setActive(true);
        onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(true);
        timeout.start(activeDuration, () => {
            setActive(false);
            onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(false);
        });
    };
    const activeString = activeText || translate("activeText");
    const copyIcon = (React.createElement(LegacyIconWrapper, { useLegacy: !(themeContext === null || themeContext === void 0 ? void 0 : themeContext.isDarkside) }, active
        ? activeIcon !== null && activeIcon !== void 0 ? activeIcon : (React.createElement(CheckmarkIcon, { "aria-hidden": !!text, title: text ? undefined : activeString, className: (themeContext === null || themeContext === void 0 ? void 0 : themeContext.isDarkside)
                ? cn("navds-copybutton__icon")
                : undefined }))
        : icon !== null && icon !== void 0 ? icon : (React.createElement(FilesIcon, { "aria-hidden": !!text, title: text ? undefined : title || translate("title"), className: (themeContext === null || themeContext === void 0 ? void 0 : themeContext.isDarkside)
                ? cn("navds-copybutton__icon")
                : undefined }))));
    if (themeContext === null || themeContext === void 0 ? void 0 : themeContext.isDarkside) {
        return (React.createElement(Button, Object.assign({ ref: ref, type: "button", className: cn("navds-copybutton", className) }, rest, { variant: variant === "action" ? "tertiary" : "tertiary-neutral", onClick: composeEventHandlers(onClick, handleClick), iconPosition: iconPosition, icon: copyIcon, "data-active": active, size: size }), text ? (active ? activeString : text) : null));
    }
    return (React.createElement("button", Object.assign({ ref: ref, type: "button" }, rest, { className: cn("navds-copybutton", className, `navds-copybutton--${size}`, `navds-copybutton--${variant}`, {
            "navds-copybutton--icon-only": !text,
            "navds-copybutton--icon-right": iconPosition === "right",
            "navds-copybutton--active": active,
        }), onClick: composeEventHandlers(onClick, handleClick) }),
        React.createElement("span", { className: cn("navds-copybutton__content") },
            iconPosition === "left" && copyIcon,
            text && (React.createElement(Label, { as: "span", size: size === "medium" ? "medium" : "small" }, active ? activeString : text)),
            iconPosition === "right" && copyIcon)));
});
function LegacyIconWrapper({ children, useLegacy, }) {
    const { cn } = useRenameCSS();
    if (!useLegacy) {
        return children;
    }
    return React.createElement("span", { className: cn("navds-copybutton__icon") }, children);
}
export default CopyButton;
//# sourceMappingURL=CopyButton.js.map